import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Bot, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getAiConfig, saveAiConfig } from '../../../lib/api';

export default function AIConfigSection() {
  const [cfg, setCfg] = useState({ ai_enabled: false, model: 'gpt-4o-mini', system_prompt: '', openai_key_set: false, masked_key: '' });
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try { setCfg(await getAiConfig()); }
    catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ai_enabled: cfg.ai_enabled, model: cfg.model, system_prompt: cfg.system_prompt };
      if (newKey.trim()) body.openai_key = newKey.trim();
      await saveAiConfig(body);
      toast.success('AI settings saved');
      setNewKey('');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-zinc-500 p-6">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="aiconfig-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Bot size={18} /> AI Chat Bot Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {cfg.openai_key_set ? <CheckCircle2 className="text-emerald-400" size={18} /> : <AlertCircle className="text-amber-400" size={18} />}
              <div className="text-sm">
                <div className="font-bold text-zinc-100">{cfg.openai_key_set ? 'OpenAI key configured' : 'No OpenAI key set'}</div>
                {cfg.openai_key_set && <div className="text-zinc-500 text-[11px] font-mono">{cfg.masked_key}</div>}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.ai_enabled} onChange={(e) => setCfg({ ...cfg, ai_enabled: e.target.checked })} className="w-4 h-4 accent-[color:var(--gold)]" data-testid="ai-enabled-toggle" />
              <span className="text-sm font-bold text-zinc-200">AI auto-reply ON</span>
            </label>
          </div>

          <p className="text-zinc-400 text-sm">When enabled, the Telegram bot will use OpenAI to auto-reply to user messages. You can still send manual replies from the Live Chat section.</p>

          <div>
            <Label className="text-zinc-300 text-xs">OpenAI API Key {cfg.openai_key_set && '(leave blank to keep existing)'}</Label>
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="sk-..." className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono" data-testid="ai-openai-key-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label className="text-zinc-300 text-xs">Model</Label>
              <select value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} className="w-full h-9 mt-1 px-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100" data-testid="ai-model-select">
                <option value="gpt-4o-mini">gpt-4o-mini (fast, cheap)</option>
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4-turbo">gpt-4-turbo</option>
                <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-zinc-300 text-xs">System Prompt (defines bot personality + scope)</Label>
              <textarea value={cfg.system_prompt} onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })} rows={6} placeholder="You are a friendly customer support agent for Acme Gaming. Answer questions about pricing, products, and onboarding. If you don't know, ask the user to contact the team via WhatsApp." className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100" data-testid="ai-prompt-input" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[color:var(--gold)] text-black font-bold" data-testid="ai-save-btn">{saving ? 'Saving…' : 'Save Settings'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
